/**
 * This file exists solely for the v1→v2 migration and should be
 * removed once v1 discount endpoints are fully deprecated.
 */

import type {PaginationMeta, TServiceApiResponse} from "../../redux/service/i-service";
import type {SingleDiscountCodeApiResponse} from "../../discounts/useCases/create-discount-code/types";
import type {DiscountListItem} from "../../discounts/useCases/get-discounts/types";
import type {GetDiscountCodeApiResponse} from "../../discounts/useCases/update-discount-code/types";
import type {TLiveClassApiResponse} from "../live-events/live-classes";
import type {TGetProductRes} from "../products";

type V2DiscountListItem = {
  id?: number;
  code?: string;
  startDate?: string;
  endDate?: string;
  usageLimit?: number;
  percentageOff?: number;
  isMultipleUse?: boolean;
  statusId?: number;
  statusName?: string;
  createdAt?: string;
  updatedAt?: string;
  offeringType?: string;
  offeringCount?: number;
  distinctCustomersUsed?: number;
  isAllOfferings?: boolean;
};

type V2DiscountDetail = {
  id?: number;
  code?: string;
  startDate?: string;
  endDate?: string;
  usageLimit?: number;
  percentageOff?: number;
  isMultipleUse?: boolean;
  isAllOfferings?: boolean;
  statusId?: number;
  statusName?: string;
  createdAt?: string;
  updatedAt?: string;
};

type V2Offering = {
  id?: string;
  offeringId?: number;
  offeringType?: string;
  offeringName?: string;
};

type GetDiscountsListV1Discount = DiscountListItem;

export function normalizeV2DiscountsList(
  v2Data: {
    discounts?: V2DiscountListItem[];
    data?: V2DiscountListItem[];
    total?: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
  },
  currentPage: number,
  perPage: number
): {data: {discounts: GetDiscountsListV1Discount[]}; meta: PaginationMeta} {
  const discounts = v2Data.discounts ?? v2Data.data ?? [];
  const total = v2Data.total ?? 0;
  const hasNextPage = v2Data.hasNextPage ?? false;
  const hasPrevPage = v2Data.hasPrevPage ?? false;

  const normalizedDiscounts: GetDiscountsListV1Discount[] = discounts.map((d) => ({
    id: d.id ?? 0,
    code: d.code ?? "",
    start_date: d.startDate ?? "",
    end_date: d.endDate ?? "",
    usage_limit: d.usageLimit ?? 0,
    percentage_off: d.percentageOff ?? 0,
    is_multiple_use: d.isMultipleUse ?? false,
    status_id: d.statusId ?? 0,
    createdAt: d.createdAt ?? "",
    updatedAt: d.updatedAt ?? "",
    deleted_at: null,
    usage_count: d.distinctCustomersUsed ?? 0,
    user_id: 0,
    statusName: d.statusName ?? "Active",
    offering_names: Array(d.offeringCount ?? 0).fill(""),
    offeringCount: d.offeringCount ?? 0,
    distinctCustomersUsed: d.distinctCustomersUsed ?? 0,
    isAllOfferings: d.isAllOfferings ?? false,
  }));

  const totalPages = Math.ceil(total / perPage) || 1;
  const meta: PaginationMeta = {
    current_page: currentPage,
    next_page: hasNextPage ? currentPage + 1 : 0,
    per_page: perPage,
    previous_page: hasPrevPage ? currentPage - 1 : 0,
    total_pages: totalPages,
  };

  return {data: {discounts: normalizedDiscounts}, meta};
}

export function normalizeV2DiscountDetail(v2Data: {
  discount?: V2DiscountDetail;
  offerings?: V2Offering[];
}): GetDiscountCodeApiResponse {
  const discount = v2Data.discount ?? {};
  const offerings = v2Data.offerings ?? [];
  const selectedOfferings = offerings
    .filter(
      (o): o is V2Offering & {offeringId: number; offeringName: string} =>
        typeof o.offeringId === "number" && typeof o.offeringName === "string"
    )
    .map((o) => ({
      offeringId: o.offeringId,
      offeringName: o.offeringName,
    }));

  const bookingOfferings: TServiceApiResponse[] = [];
  const liveClassOfferings: TLiveClassApiResponse[] = [];
  const productOfferings: TGetProductRes[] = [];
  const productServiceOfferings: TGetProductRes[] = [];
  const subscriptionOfferings: TGetProductRes[] = [];

  for (const o of offerings) {
    const offeringItem = {
      id: o.offeringId ?? 0,
      name: o.offeringName ?? "",
    } as TServiceApiResponse & TLiveClassApiResponse & TGetProductRes;
    if (o.offeringType === "booking") {
      bookingOfferings.push(offeringItem as TServiceApiResponse);
    } else if (o.offeringType === "live-class" || o.offeringType === "liveClass") {
      liveClassOfferings.push(offeringItem as TLiveClassApiResponse);
    } else if (o.offeringType === "product") {
      productOfferings.push(offeringItem as TGetProductRes);
    } else if (
      o.offeringType === "product-service" ||
      o.offeringType === "productService"
    ) {
      productServiceOfferings.push(offeringItem as TGetProductRes);
    } else if (o.offeringType === "subscription" || o.offeringType === "subscriptions") {
      subscriptionOfferings.push(offeringItem as TGetProductRes);
    }
  }

  const singleDiscount: SingleDiscountCodeApiResponse = {
    id: discount.id ?? 0,
    code: discount.code ?? "",
    start_date: discount.startDate ?? "",
    end_date: discount.endDate ?? "",
    usage_limit: discount.usageLimit ?? 0,
    percentage_off: discount.percentageOff ?? 0,
    is_multiple_use: discount.isMultipleUse ?? false,
    status_id: discount.statusId ?? 0,
    createdAt: discount.createdAt ?? "",
    updatedAt: discount.updatedAt ?? "",
    deleted_at: null,
    usage_count: 0,
    user_id: 0,
  };

  return {
    discount: singleDiscount,
    bookingOfferings,
    liveClassOfferings,
    productOfferings,
    productServiceOfferings,
    subscriptionOfferings,
    selectedOfferings,
    isAllOfferings: discount.isAllOfferings ?? false,
  };
}
